import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';

// Import homepage components
import {
  HeroSection,
  IntegrationCarousel,
  IntegrationsGrid,
  MultimodalPreview,
  MemorySharing,
  TrustSignals,
  FinalCTA,
  type Integration,
  type HeroIntegration,
  type MultimodalFeature
} from '../components/home';
import DctwOffer from '../components/home/DctwOffer';

// Import integration utilities
import { getIntegration } from '../integrations/registry';

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  // Helper function to get integration landing page path
  const getIntegrationLandingPath = (integrationName: string) => {
    const nameToId: { [key: string]: string } = {
      'Claude Desktop': 'claude_desktop',
      'ChatGPT Actions': 'custom_gpt_action',
      'WhatsApp Basic': 'whatsapp_basic',
      'Cursor': 'cursor',
      'Bare API': 'bare_api',
      'Bare MCP': 'bare_mcp',
      'X (formerly Twitter) Bookmarks': 'twitter_bookmarks',
      'Slack Bot': 'slack_bot',
      'Discord Bot': 'discord_bot',
      'Microsoft Teams': 'microsoft_teams',
      'Telegram Bot': 'telegram',
      'n8n': 'n8n',
      'Zapier': 'zapier',
      'Make.com': 'make',
      'WhatsApp Business': 'whatsapp_business',
      'Notion': 'notion',
      'GitHub': 'github',
      'Google Workspace': 'google_workspace',
      'IFTTT': 'ifttt',
      'Mobile Apps': 'ios'
    };
    
    const integrationId = nameToId[integrationName];
    if (integrationId) {
      const integration = getIntegration(integrationId);
      return integration?.components?.landingPagePath;
    }
    return null;
  };

  // Handler for integration card clicks
  const handleIntegrationClick = (integration: Integration) => {
    if (integration.landingPath?.startsWith('mailto:')) {
      window.location.href = integration.landingPath;
      return;
    }
    if (integration.landingPath) {
      navigate(integration.landingPath);
      return;
    }
    const landingPath = getIntegrationLandingPath(integration.name);
    if (landingPath) {
      navigate(landingPath);
    } else {
      navigate('/integrations');
    }
  };

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Integration data for the homepage
  const integrations: Integration[] = [
    { 
      id: 'claude_desktop',
      name: t('integrations.claudeDesktop.name', { ns: 'homepage' }), 
      icon: "anthropic" as const, 
      status: 'ready', 
      category: 'aiAssistants',
      description: t('integrations.claudeDesktop.description', { ns: 'homepage' }),
      landingPath: '/integrations/claude-desktop'
    },
    { 
      id: 'custom_gpt_action',
      name: t('integrations.chatgptActions.name', { ns: 'homepage' }), 
      icon: "openai" as const, 
      status: 'ready', 
      category: 'aiAssistants',
      description: t('integrations.chatgptActions.description', { ns: 'homepage' }),
      landingPath: '/integrations/chatgpt-custom-actions'
    },
    { 
      id: 'whatsapp_basic',
      name: t('integrations.whatsappBasic.name', { ns: 'homepage' }), 
      icon: "whatsapp" as const, 
      status: 'ready', 
      category: 'communication',
      description: t('integrations.whatsappBasic.description', { ns: 'homepage' }),
      landingPath: '/integrations/whatsapp-basic'
    },
    { 
      id: 'cursor',
      name: t('integrations.cursor.name', { ns: 'homepage' }), 
      icon: "cursor" as const, 
      status: 'ready', 
      category: 'development',
      description: t('integrations.cursor.description', { ns: 'homepage' }),
      landingPath: '/integrations/cursor',
      isNew: true
    },
    { 
      id: 'bare_api',
      name: t('integrations.bareApi.name', { ns: 'homepage' }), 
      icon: "api" as const, 
      status: 'ready', 
      category: 'development',
      description: t('integrations.bareApi.description', { ns: 'homepage' }),
      landingPath: '/integrations/bare-api'
    },
    { 
      id: 'bare_mcp',
      name: t('integrations.bareMcp.name', { ns: 'homepage' }), 
      icon: "mcp" as const, 
      status: 'ready', 
      category: 'development',
      description: t('integrations.bareMcp.description', { ns: 'homepage' }),
      landingPath: '/integrations/bare-mcp'
    },
    {
      id: 'dify',
      name: t('integrations.dify.name', { ns: 'homepage' }),
      icon: 'dify' as const,
      status: 'ready',
      category: 'automation',
      description: t('integrations.dify.description', { ns: 'homepage' }),
      landingPath: '/integrations/dify',
    },
    {
      id: 'obsidian',
      name: t('integrations.obsidian.name', { ns: 'homepage' }),
      icon: 'obsidian' as const,
      status: 'beta',
      category: 'productivity',
      description: t('integrations.obsidian.description', { ns: 'homepage' }),
      landingPath: 'mailto:ali@memara.io',
    },
    {
      id: 'python_sdk',
      name: t('integrations.pythonSdk.name', { ns: 'homepage' }),
      icon: 'python' as const,
      status: 'ready',
      category: 'development',
      description: t('integrations.pythonSdk.description', { ns: 'homepage' }),
      landingPath: '/blog/memara-python-sdk-launch',
    },
    { 
      id: 'chrome_extension',
      name: t('integrations.chromeExtensionIntegration.name', { ns: 'homepage' }), 
      icon: "googlechrome" as const, 
      status: 'ready', 
      category: 'browser',
      description: t('integrations.chromeExtensionIntegration.description', { ns: 'homepage' }),
      landingPath: '/integrations/chrome-extension',
      isNew: true
    },
    { 
      id: 'twitter_bookmarks',
      name: t('integrations.twitterBookmarks.name', { ns: 'homepage' }), 
      icon: "twitter" as const, 
      status: 'ready', 
      category: 'socialMedia',
      description: t('integrations.twitterBookmarks.description', { ns: 'homepage' }),
      landingPath: '/integrations/x-bookmarks',
      isNew: true
    },
    { 
      id: 'slack_bot',
      name: t('integrations.slackBot.name', { ns: 'homepage' }), 
      icon: "slack" as const, 
      status: 'coming', 
      category: 'communication',
      description: t('integrations.slackBot.description', { ns: 'homepage' })
    },
    { 
      id: 'discord_bot',
      name: t('integrations.discordBot.name', { ns: 'homepage' }), 
      icon: "discord" as const, 
      status: 'coming', 
      category: 'communication',
      description: t('integrations.discordBot.description', { ns: 'homepage' })
    },
    { 
      id: 'n8n',
      name: t('integrations.n8n.name', { ns: 'homepage' }), 
      icon: "n8n" as const, 
      status: 'ready', 
      category: 'automation',
      description: t('integrations.n8n.description', { ns: 'homepage' }),
      landingPath: '/integrations/n8n',
      isNew: true
    },
    { 
      id: 'zapier',
      name: t('integrations.zapier.name', { ns: 'homepage' }), 
      icon: "zapier" as const, 
      status: 'ready', 
      category: 'automation',
      description: t('integrations.zapier.description', { ns: 'homepage' }),
      landingPath: '/integrations/zapier',
      isNew: true
    },
    { 
      id: 'make',
      name: t('integrations.makeCom.name', { ns: 'homepage' }), 
      icon: "make" as const, 
      status: 'development', 
      category: 'automation',
      description: t('integrations.makeCom.description', { ns: 'homepage' }),
      progressPercentage: 45
    },
    { 
      id: 'microsoft_teams',
      name: t('integrations.microsoftTeams.name', { ns: 'homepage' }), 
      icon: "microsoftteams" as const, 
      status: 'coming', 
      category: 'communication',
      description: t('integrations.microsoftTeams.description', { ns: 'homepage' })
    },
    { 
      id: 'telegram',
      name: t('integrations.telegramBot.name', { ns: 'homepage' }), 
      icon: "telegram" as const, 
      status: 'coming', 
      category: 'communication',
      description: t('integrations.telegramBot.description', { ns: 'homepage' })
    },
    { 
      id: 'notion',
      name: t('integrations.notion.name', { ns: 'homepage' }), 
      icon: "notion" as const, 
      status: 'coming', 
      category: 'productivity',
      description: t('integrations.notion.description', { ns: 'homepage' })
    },
    { 
      id: 'github',
      name: t('integrations.github.name', { ns: 'homepage' }), 
      icon: "github" as const, 
      status: 'coming', 
      category: 'development',
      description: t('integrations.github.description', { ns: 'homepage' })
    },
    { 
      id: 'google_workspace',
      name: t('integrations.googleWorkspace.name', { ns: 'homepage' }), 
      icon: "google" as const, 
      status: 'coming', 
      category: 'productivity',
      description: t('integrations.googleWorkspace.description', { ns: 'homepage' })
    },
    { 
      id: 'ifttt',
      name: t('integrations.ifttt.name', { ns: 'homepage' }), 
      icon: "ifttt" as const, 
      status: 'coming', 
      category: 'automation',
      description: t('integrations.ifttt.description', { ns: 'homepage' })
    },
    { 
      id: 'whatsapp_business',
      name: t('integrations.whatsappBusiness.name', { ns: 'homepage' }), 
      icon: "whatsapp", 
      status: 'coming', 
      category: 'communication',
      description: t('integrations.whatsappBusiness.description', { ns: 'homepage' })
    },
    { 
      id: 'ios',
      name: t('integrations.mobileApps.name', { ns: 'homepage' }), 
      icon: "ios", 
      status: 'concept', 
      category: 'mobile',
      description: t('integrations.mobileApps.description', { ns: 'homepage' })
    }
  ];

  // Multimodal features data
  const multimodalFeatures: MultimodalFeature[] = [
    {
      id: 'visual-memory',
      name: t('multimodal.visual.title', { ns: 'homepage' }),
      description: t('multimodal.visual.description', { ns: 'homepage' }),
      icon: 'image',
      emoji: '🖼️',
      releaseQuarter: t('multimodal.visual.release', { ns: 'homepage' }),
      status: 'development',
      progressPercentage: 65,
      capabilities: [],
      gradientFrom: 'orange-400',
      gradientTo: 'orange-600',
      borderColor: 'orange-200',
      accentColor: 'orange-500',
      earlyAccessUrl: '/roadmap/image-memories#image-interest'
    },
    {
      id: 'audio-memory',
      name: t('multimodal.voice.title', { ns: 'homepage' }),
      description: t('multimodal.voice.description', { ns: 'homepage' }),
      icon: 'audio-lines',
      emoji: '🎵',
      releaseQuarter: t('multimodal.voice.release', { ns: 'homepage' }),
      status: 'beta',
      progressPercentage: 85,
      capabilities: [
        t('multimodal.voice.features.speechToText', { ns: 'homepage' }),
        t('multimodal.voice.features.semanticSearch', { ns: 'homepage' }),
        t('multimodal.voice.features.speakerIdentification', { ns: 'homepage' })
      ],
      gradientFrom: 'blue-400',
      gradientTo: 'blue-600',
      borderColor: 'blue-200',
      accentColor: 'blue-500',
      earlyAccessUrl: '/roadmap/audio-memories'
    },
    {
      id: 'video-memory',
      name: t('multimodal.video.title', { ns: 'homepage' }),
      description: t('multimodal.video.description', { ns: 'homepage' }),
      icon: 'video',
      emoji: '🎥',
      releaseQuarter: t('multimodal.video.release', { ns: 'homepage' }),
      status: 'concept',
      progressPercentage: 15,
      capabilities: [],
      gradientFrom: 'purple-400',
      gradientTo: 'purple-600',
      borderColor: 'purple-200',
      accentColor: 'purple-500',
      earlyAccessUrl: '/roadmap/image-memories#image-interest'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      <DctwOffer variant="banner" />
      <SEOHead
        title={t('seo.title', { ns: 'homepage' }) as string}
        description={t('seo.description', { ns: 'homepage' }) as string}
        keywords={[
          'memara',
          'n8n mem0',
          'mem0 n8n',
          'simple memory n8n',
          'n8n ai memory',
          'persistent ai memory',
          'persistent memory ai',
          'n8n multi-tenant architecture',
          'AI memory API',
          'semantic search memory',
          'vector database vs traditional database',
          'mem0 alternative',
          'memara vs mem0'
        ]}
        ogType="website"
        priority="high"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: t('seo.structuredData.name', { ns: 'homepage' }) as string,
          description: t('seo.structuredData.description', { ns: 'homepage' }) as string,
          about: [
            'n8n AI memory',
            'mem0 n8n',
            'persistent AI memory',
            'multi-tenant memory architecture'
          ]
        }}
      />

      <div className="sr-only">
        {t('seo.srOnly', { ns: 'homepage' }) as string}
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="container mx-auto px-4 py-16 space-y-16"
      >
        {/* Hero Section */}
        <motion.section variants={itemVariants}>
          <HeroSection
            showWizardInstall
            featuredIntegrations={integrations.filter(i => i.status === 'ready').map(i => ({
              id: i.id,
              name: i.name,
              icon: i.icon,
              status: i.status === 'ready' ? 'ready' as const : i.status === 'coming' ? 'coming' as const : 'development' as const,
              landingPath: i.landingPath
            }))}
            maxIntegrations={8}
            showCentralHub={true}
            secondaryCTA={{
              text: t('hero.cta.browseIntegrations', { ns: 'homepage' }),
              href: '#integrations',
            }}
            onIntegrationClick={(integration: HeroIntegration) => {
              if (integration.landingPath?.startsWith('mailto:')) {
                window.location.href = integration.landingPath;
                return;
              }
              if (integration.landingPath) {
                navigate(integration.landingPath);
                return;
              }
              const landingPath = getIntegrationLandingPath(integration.name);
              if (landingPath) {
                navigate(landingPath);
              } else {
                navigate('/integrations');
              }
            }}
          />
        </motion.section>

        <motion.section variants={itemVariants}>
          <DctwOffer variant="section" />
        </motion.section>

        {/* Full integrations catalog */}
        <motion.section
          id="integrations"
          variants={itemVariants}
          aria-label={t('integrations.choosePlatform', { ns: 'homepage' })}
        >
          <IntegrationsGrid
            integrations={integrations}
            showAll
            showCategories
            enableFiltering
            variant="homepage"
            columns={{ mobile: 2, tablet: 2, desktop: 3 }}
            onIntegrationClick={handleIntegrationClick}
            enableAnimations
          />
        </motion.section>

        {/* Multimodal Memory Preview */}
        <MultimodalPreview
          features={multimodalFeatures}
          variant={isMobile ? 'mobileLight' : 'homepage'}
        />

        {/* Memory Sharing & Isolation */}
        <MemorySharing />

        {/* Demo section - demoted below differentiator content */}
        <motion.section id="demo" variants={itemVariants} className="space-y-8" aria-label="Product demo">
          <IntegrationCarousel enableAnimations={true} />
        </motion.section>

        {/* Trust Signals */}
        <TrustSignals />

        {/* Final CTA */}
        <FinalCTA
          pricingLink={{
            text: t('finalCta.pricingLink', { ns: 'homepage' }),
            href: '/pricing',
          }}
        />
      </motion.div>
    </div>
  );
} 